import React, { useEffect } from "react";
import api from "../api";
import { useLocalStorage } from "./useLocalStorage";

import jwtDecode from "jwt-decode";
import { ITokenResponse } from "../api/services/auth.service";
import { ITeam } from "../api/services/team.service";

export interface IAuthenticatedUser {
  id: string;
  email: string;
  authProviderId: string;
  firstname: string;
  lastname: string;
  verified: boolean;
  onboarded: boolean;
  authorities: string[];
  isGuest: boolean;
}

interface AuthContextType {
  isAuthenticated: boolean;
  user: IAuthenticatedUser | null;
  team: ITeam | null;
  logout: () => void;
  handleOAuthCallback: ({
    provider,
    code,
  }: {
    provider: string;
    code: string;
  }) => Promise<ITokenResponse>;
  login: ({
    email,
    password,
  }: {
    email: string;
    password: string;
  }) => Promise<boolean>;
  updateUser: () => void;
  setAccessToken: (accessToken: string | null) => void;
  setRefreshToken: (refreshToken: string | null) => void;
}

let AuthContext = React.createContext<AuthContextType>(null!);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [accessToken, setAccessToken] = useLocalStorage<string | null>(
    "accessToken",
    null
  );
  const [refreshToken, setRefreshToken] = useLocalStorage<string | null>(
    "refreshToken",
    null
  );

  const [recent, setRecent] = useLocalStorage<any[]>("recentSearches", []);

  let [user, setUser] = React.useState<IAuthenticatedUser | null>(null);
  let [team, setTeam] = React.useState<ITeam | null>(null);
  let [isAuthenticated, setIsAuthenticated] = React.useState<any>(
    accessToken !== null
  );

  console.log("-- RERENDER Auth", "ALG");

  useEffect(() => {
    console.log("UseAuth: useEffect(accessToken) - setIsAuthenticated");
    setIsAuthenticated(accessToken !== null);
    if (accessToken !== null) {
      fetchUser(accessToken);
    }
  }, [accessToken]);

  interface JwtPayload {
    authorities: string[];
    uid: string;
  }

  // async function fetchSubscriptions() {
  //   const { data, status } = await api.subscription.getMySubscriptions();
  //   if (status === 200 && data.result) {
  //     setActiveSubscriptions(data.result);
  //   }
  // }

  let fetchUser = async (accessToken: string) => {
    const tokenInfo = jwtDecode<JwtPayload>(accessToken);
    const authorities = tokenInfo.authorities;
    if (authorities.indexOf("ADMIN") === -1) {
      setIsAuthenticated(false);
      setUser(null);
    }
    console.log("UseAuth: fetchUser - found user, getting data");
    try {
      const [userResponse, team] = await Promise.all([
        api.user.getMyUser(),
        api.team.getMyTeam(),
      ]);
      if (team.status === 200 && team.data.result) {
        setTeam(team.data.result);
      }
      console.log("UseAuth: fetchUser - fetching user data done");
      if (userResponse.status === 200 && userResponse.data.result) {
        const {
          firstname,
          lastname,
          verified,
          id,
          email,
          onboarded,
          authProviderId,
        } = userResponse.data.result;
        setUser({
          firstname,
          lastname,
          verified,
          id,
          authorities,
          onboarded,
          authProviderId,
          email,
          isGuest: false,
        });
      } else {
        console.log(userResponse.data);
        setUser(null);
      }
      // setLoading(false);
      console.log("after loading false");
    } catch (e) {
      logout();
    }
  };

  let updateUser = async () => {
    if (isAuthenticated && accessToken) {
      fetchUser(accessToken);
    }
  };

  let logout = async () => {
    setAccessToken(null);
    setRefreshToken(null);
    setRecent([]);
    setIsAuthenticated(false);
  };

  let login = async ({
    email,
    password,
  }: {
    email: string;
    password: string;
  }) => {
    const { data, status } = await api.auth.login(email, password);
    if (status === 200 && data.result?.access_token) {
      setAccessToken(data.result.access_token);
      setRefreshToken(data.result.refresh_token);
      return true;
    } else {
      setUser(null);
      return false;
    }
  };

  let handleOAuthCallback = async ({
    provider,
    code,
  }: {
    provider: string;
    code: string;
  }) => {
    const { data, status } = await api.auth.oAuth(provider, code);
    if (status === 200 && data.result?.access_token) {
      console.log("oauth callback, got token");
      setAccessToken(data.result.access_token);
      setRefreshToken(data.result.refresh_token);
      return data.result;
    } else {
      setUser(null);
      throw new Error("OAuth Error");
    }
  };

  let value = {
    isAuthenticated,
    handleOAuthCallback,
    logout,
    login,
    user,
    updateUser,
    setAccessToken,
    setRefreshToken,
    team,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  return React.useContext(AuthContext);
}
